/* eslint-disable react/no-unescaped-entities */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Megaphone, Sparkles, CheckCircle, Send, Trash2, Loader2,
  ArrowLeft, Calendar, Image as ImageIcon, AlertTriangle, RefreshCw,
} from "lucide-react";
import { format } from "date-fns";

// iter-127g: the production frontend at vivomusicianmanagement.co.uk
// historically called the emergent.host backend cross-origin. Even with
// correct CORS in FastAPI, the Cloudflare ingress in front of the
// production preview-style backend silently rewrites response headers
// to wildcard '*' (stripping `access-control-allow-credentials: true`),
// which the browser then rejects for Authorization-bearing requests —
// surfacing as the user's recurring "Network Error" with no actionable
// info in DevTools.
//
// iter-128b: same-origin logic now lives in the shared `src/lib/api.js`
// module. We still keep a *separate* axios instance here because the
// SysAdmin console uses its own JWT (localStorage `systemAdminToken`)
// distinct from the band-scoped user token.
import { API as _API } from "../lib/api";
const TOKEN_KEY = "systemAdminToken";

const api = axios.create({ baseURL: _API });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const STATUS_COLORS = {
  draft:    "bg-slate-500/20 text-slate-300 border-slate-500/40",
  approved: "bg-blue-500/20 text-blue-300 border-blue-500/40",
  posted:   "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  failed:   "bg-red-500/20 text-red-300 border-red-500/40",
};

export default function SystemAdminMarketing() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [fbStatus, setFbStatus] = useState(null);
  const [categories, setCategories] = useState([]);
  const [posts, setPosts] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [generating, setGenerating] = useState(false);
  const [seedingDemo, setSeedingDemo] = useState(false);
  const [newDraft, setNewDraft] = useState({ body: "", category: "feature" });
  // iter-118: AI-from-prompt state
  const [aiPrompt, setAiPrompt] = useState("");
  const [promptCount, setPromptCount] = useState(1);
  const [promptGenerating, setPromptGenerating] = useState(false);
  // iter-120: per-generation image options (shared by both AI generators)
  const [imageOpts, setImageOpts] = useState({
    count: 1,
    pages: [],            // empty = AI picks from all available
    views: ["desktop", "mobile"],
    themes: ["dark", "light"],
  });
  const [imageMeta, setImageMeta] = useState(null); // { pages, views, themes }
  // Per-post image regen state — keyed by post.id, used by the gallery.
  const [imageEditPostId, setImageEditPostId] = useState(null);
  const [imageEditOpts, setImageEditOpts] = useState({ count: 1, pages: [], views: ["desktop","mobile"], themes: ["dark","light"] });
  const [imageEditSaving, setImageEditSaving] = useState(false);
  // iter-126m: capture-pipeline diagnostic state
  const [diagnosing, setDiagnosing] = useState(false);
  const [diagnoseReport, setDiagnoseReport] = useState(null);
  // iter-126o: chromium install state — when the diagnostic shows the
  // binary isn't ready yet, we offer a "Install Chromium" button that
  // kicks off a background install + polls the status endpoint so the
  // request itself never hits the Cloudflare timeout.
  const [installingChromium, setInstallingChromium] = useState(false);
  const [chromiumStatus, setChromiumStatus] = useState(null);
  // iter-127f: deep diagnostic (chromium launch + login + capture) runs
  // in the background and is polled — exactly like the chromium install
  // — so it can never hit the Cloudflare ~100s ingress timeout that was
  // turning "Run diagnostic" into a Network Error in production.
  const [runningDeep, setRunningDeep] = useState(false);
  const [deepStatus, setDeepStatus] = useState(null);
  // iter-135j — track drafts that still carry an AI-failure placeholder
  // so we can offer a one-click "Regenerate failed slots" button.
  const [failedIds, setFailedIds] = useState([]);
  const [regeneratingFailed, setRegeneratingFailed] = useState(false);
  const [lastPrompt, setLastPrompt] = useState("");

  const runCaptureDiagnostic = async () => {
    setDiagnosing(true);
    setDiagnoseReport(null);
    try {
      // iter-127f: this endpoint now only does static checks (env,
      // filesystem, playwright module, chromium binary on disk). It
      // returns in <2s, so it can never time out at the ingress.
      const res = await api.get("/system/marketing/diagnose", { timeout: 15000 });
      setDiagnoseReport(res.data);
      const failed = (res.data.steps || []).filter((s) => !s.ok);
      if (failed.length === 0) {
        toast.success("Static checks all green. Click 'Run full pipeline test' for the end-to-end probe.");
      } else {
        const chromiumMissing = failed.some((s) => s.step === "chromium_binary_present");
        if (chromiumMissing) {
          toast.error("Chromium binary not yet downloaded. Click 'Install Chromium' to fix.", { duration: 8000 });
        } else {
          toast.error(`Diagnostic found ${failed.length} failed step(s). Copy the JSON and share it.`);
        }
      }
    } catch (e) {
      toast.error(`Diagnostic call failed: ${e?.response?.data?.detail || e.message}`);
      setDiagnoseReport({ error: e?.response?.data || e.message });
    } finally {
      setDiagnosing(false);
    }
  };

  const runDeepDiagnostic = async () => {
    setRunningDeep(true);
    setDeepStatus({ status: "queued", message: "Queued — running in background, you can stay on this page…" });
    try {
      await api.post("/system/marketing/diagnose/run-deep");
      toast.success("Deep diagnostic queued — polling for completion…");
      const started = Date.now();
      let stopped = false;
      while (!stopped && Date.now() - started < 240000) {
        await new Promise((r) => setTimeout(r, 5000));
        try {
          const res = await api.get("/system/marketing/diagnose/deep-status");
          setDeepStatus(res.data);
          if (!res.data.running && res.data.report) {
            stopped = true;
            const failed = (res.data.report.steps || []).filter((s) => !s.ok);
            if (failed.length === 0) {
              toast.success("Full pipeline test passed — captures working end-to-end.");
            } else {
              toast.error(`Pipeline test found ${failed.length} failed step(s). Check the deep status panel.`);
            }
          }
        } catch (e) {
          // continue polling on transient errors
        }
      }
    } catch (e) {
      toast.error(`Deep diagnostic failed to queue: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRunningDeep(false);
    }
  };

  const installChromium = async () => {
    setInstallingChromium(true);
    setChromiumStatus({ status: "queued", message: "Starting background install (no need to wait on this page)…" });
    try {
      await api.post("/system/marketing/install-chromium");
      toast.success("Chromium install queued — polling for completion (~60s)…");
      // Poll every 5 s, up to 3 minutes
      const started = Date.now();
      let stopped = false;
      while (!stopped && Date.now() - started < 180000) {
        await new Promise((r) => setTimeout(r, 5000));
        try {
          const res = await api.get("/system/marketing/install-chromium/status");
          setChromiumStatus(res.data);
          if (!res.data.running && res.data.result) {
            stopped = true;
            if (res.data.result.ok) {
              toast.success("Chromium installed. Re-run the diagnostic.");
            } else {
              toast.error(`Install failed: ${res.data.result.error || "unknown error"}`);
            }
          }
        } catch (e) {
          // Continue polling on transient errors
        }
      }
    } catch (e) {
      toast.error(`Install request failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setInstallingChromium(false);
    }
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, postsRes, imgOptsRes, failedRes] = await Promise.all([
        api.get("/system/marketing/config"),
        api.get("/system/marketing/posts", {
          params: { status: statusFilter && statusFilter !== "all" ? statusFilter : undefined },
        }),
        api.get("/system/marketing/image-options"),
        api.get("/system/marketing/failed-drafts").catch(() => ({ data: { ids: [] } })),
      ]);
      setConfig(cfgRes.data.config);
      setFbStatus(cfgRes.data.facebook);
      setCategories(cfgRes.data.categories || []);
      setPosts(postsRes.data.posts || []);
      setImageMeta(imgOptsRes.data);
      setFailedIds(failedRes.data?.ids || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("System admin session expired");
        navigate("/system-admin");
      } else {
        toast.error(err?.response?.data?.detail || "Failed to load marketing data");
      }
    } finally {
      setLoading(false);
    }
  }, [statusFilter, navigate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const updateConfig = async (changes) => {
    try {
      const res = await api.put("/system/marketing/config", changes);
      setConfig(res.data.config);
      toast.success("Schedule updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  // iter-137: long operations (LLM drafting, Browserbase captures) run as
  // backend background jobs — we poll until done so the request itself
  // never hits Cloudflare's ~100s edge timeout (the prod 524 bug).
  const pollJob = async (jobId, { intervalMs = 3000, timeoutMs = 300000 } = {}) => {
    const deadline = Date.now() + timeoutMs;
    for (;;) {
      await new Promise((r) => setTimeout(r, intervalMs));
      let row;
      try {
        row = (await api.get(`/system/marketing/jobs/${jobId}`)).data;
      } catch {
        if (Date.now() > deadline) throw new Error("Job polling timed out");
        continue; // transient network blip — keep polling
      }
      if (row.status === "done") return row.result || {};
      if (row.status === "failed") throw new Error(row.error || "Job failed");
      if (Date.now() > deadline) throw new Error("Job timed out — try again");
    }
  };

  // iter-137b: create/refresh the Riverside Brass demo band in THIS
  // environment so screenshot captures have a real dashboard to shoot.
  const seedDemoBand = async () => {
    if (!window.confirm("Create (or refresh) the Riverside Brass demo band in this environment? Takes ~1-2 min.")) return;
    setSeedingDemo(true);
    try {
      const res = await api.post("/system/marketing/seed-demo-band");
      toast.info("Seeding demo band — runs in the background…");
      await pollJob(res.data.job_id, { timeoutMs: 360000 });
      toast.success("Riverside Brass demo band is ready for screenshots");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Seeding failed");
    } finally {
      setSeedingDemo(false);
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await api.post("/system/marketing/generate", {
        count: 5,
        images: imageOpts,
      });
      toast.info("Drafting with AI — this runs in the background…");
      const result = await pollJob(res.data.job_id);
      toast.success(`${result.count ?? 0} drafts ready to review`);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "AI generation failed");
    } finally {
      setGenerating(false);
    }
  };

  // iter-118: AI-from-prompt — owner types what they want, Claude drafts
  // it, the result lands in the queue as a regular draft (approval flow
  // unchanged).
  const generateFromPrompt = async () => {
    if (!aiPrompt.trim()) {
      toast.error("Type what you'd like the post to be about");
      return;
    }
    setPromptGenerating(true);
    try {
      const res = await api.post("/system/marketing/generate-from-prompt", {
        prompt: aiPrompt.trim(),
        count: promptCount,
        images: imageOpts,
      });
      toast.info("Drafting with AI — this runs in the background…");
      const result = await pollJob(res.data.job_id);
      const n = result.count ?? 0;
      toast.success(`${n} prompt-based draft${n === 1 ? "" : "s"} created`);
      setLastPrompt(aiPrompt.trim());
      setAiPrompt("");
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "AI generation failed");
    } finally {
      setPromptGenerating(false);
    }
  };

  // iter-135j — one-click retry for any draft whose body is still an
  // "[AI draft N timed out / failed]" placeholder. Uses the last prompt
  // the sys-admin typed (falls back to the generic system prompt).
  const regenerateFailedSlots = async () => {
    setRegeneratingFailed(true);
    try {
      const res = await api.post("/system/marketing/regenerate-failed", {
        prompt: lastPrompt || "",
      });
      if (!res.data?.job_id) {
        toast.info("No failed drafts to regenerate");
        fetchAll();
        return;
      }
      toast.info("Regenerating failed drafts in the background…");
      const { regenerated = 0, still_failed = 0 } = await pollJob(res.data.job_id);
      if (regenerated > 0 && still_failed === 0) {
        toast.success(`Regenerated ${regenerated} draft${regenerated === 1 ? "" : "s"}`);
      } else if (regenerated > 0) {
        toast.warning(
          `Regenerated ${regenerated}, but ${still_failed} still failed. Try again in a moment.`
        );
      } else {
        toast.error("Regeneration failed — try again or rewrite manually.");
      }
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Regenerate failed");
    } finally {
      setRegeneratingFailed(false);
    }
  };

  // iter-120: per-post image edit. Opens a small panel that lets the user
  // regenerate the image set OR remove individual images.
  const openImageEditor = (post) => {
    setImageEditPostId(post.id);
    setImageEditOpts({
      count: (post.images && post.images.length) || (post.image_url ? 1 : 0),
      pages: [],
      views: ["desktop", "mobile"],
      themes: ["dark", "light"],
    });
  };
  const regenImagesForPost = async () => {
    if (!imageEditPostId) return;
    setImageEditSaving(true);
    try {
      const res = await api.put(`/system/marketing/posts/${imageEditPostId}/images`, {
        regenerate: imageEditOpts,
      });
      toast.info("Capturing live app screenshots — takes ~30-60s…");
      const result = await pollJob(res.data.job_id);
      toast.success(`Captured ${result.images_count ?? 0} fresh screenshot${(result.images_count ?? 0) === 1 ? "" : "s"}`);
      setImageEditPostId(null);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Image update failed");
    } finally {
      setImageEditSaving(false);
    }
  };
  const removeImageFromPost = async (post, idx) => {
    const next = (post.images || []).filter((_, i) => i !== idx);
    try {
      await api.put(`/system/marketing/posts/${post.id}/images`, { images: next });
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };
  const clearImagesFromPost = async (post) => {
    try {
      await api.put(`/system/marketing/posts/${post.id}/images`, { images: [] });
      toast.success("Post will publish without photos");
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const createDraft = async () => {
    if (!newDraft.body.trim()) {
      toast.error("Write something first");
      return;
    }
    try {
      await api.post("/system/marketing/posts", newDraft);
      toast.success("Draft saved");
      setNewDraft({ body: "", category: "feature" });
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const approve = async (post) => {
    try {
      await api.post(`/system/marketing/posts/${post.id}/approve`, {});
      toast.success("Approved — will publish on the next scheduled slot");
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Approve failed");
    }
  };

  const publishNow = async (post) => {
    if (!window.confirm(`Publish this post to Facebook RIGHT NOW?\n\n"${post.body.slice(0, 80)}..."`)) return;
    try {
      const res = await api.post(`/system/marketing/posts/${post.id}/publish-now`);
      toast.success(`Posted! ${res.data.url ? "(view link in queue)" : ""}`);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Publish failed");
    }
  };

  const updatePost = async (post, changes) => {
    try {
      await api.put(`/system/marketing/posts/${post.id}`, changes);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (post) => {
    if (!window.confirm("Delete this draft permanently?")) return;
    try {
      await api.delete(`/system/marketing/posts/${post.id}`);
      toast.success("Deleted");
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-6xl mx-auto p-4 sm:p-8 space-y-6">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Button
              variant="ghost" size="sm"
              onClick={() => navigate("/system-admin")}
              data-testid="back-to-sysadmin"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <Megaphone className="w-6 h-6 text-primary shrink-0" />
            <h1 className="text-xl sm:text-2xl font-semibold truncate">
              Facebook Marketing
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline" size="sm"
              onClick={seedDemoBand} disabled={seedingDemo}
              data-testid="seed-demo-band-btn"
            >
              {seedingDemo ? "Seeding…" : "Seed demo band"}
            </Button>
            <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
        </div>

        {/* Facebook status */}
        {fbStatus && (
          <Card className={fbStatus.ok ? "border-emerald-500/40" : "border-red-500/40"}>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                {fbStatus.ok ? <CheckCircle className="w-4 h-4 text-emerald-400" /> : <AlertTriangle className="w-4 h-4 text-red-400" />}
                Facebook connection
              </CardTitle>
            </CardHeader>
            <CardContent className="text-xs space-y-1">
              {fbStatus.ok ? (
                <>
                  <div>App: <strong>{fbStatus.application}</strong> · Page: <code className="text-xs">{fbStatus.profile_id}</code></div>
                  <div>Token: <Badge variant="outline">{fbStatus.type}</Badge> · Never expires</div>
                  {fbStatus.data_access_expires_at && (
                    <div className="text-muted-foreground">
                      Data-access renewal date: {format(new Date(fbStatus.data_access_expires_at * 1000), "PP")}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-red-300">
                  {fbStatus.error || "Connection failed. Re-paste the Page Access Token in your deploy Secrets."}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Schedule config */}
        {config && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Calendar className="w-4 h-4 text-primary" /> Schedule
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Day</Label>
                <Select
                  value={String(config.weekly_day)}
                  onValueChange={(v) => updateConfig({ weekly_day: Number(v) })}
                >
                  <SelectTrigger data-testid="marketing-day-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DAYS.map((d, i) => (
                      <SelectItem key={i} value={String(i)}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Hour (UK)</Label>
                <Input
                  type="number" min={0} max={23}
                  value={config.weekly_hour}
                  onChange={(e) => updateConfig({ weekly_hour: Number(e.target.value) })}
                  data-testid="marketing-hour-input"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox" id="auto-publish"
                  checked={!!config.auto_publish}
                  onChange={(e) => updateConfig({ auto_publish: e.target.checked })}
                />
                <Label htmlFor="auto-publish" className="text-sm">Auto-publish approved posts</Label>
              </div>
              <div className="sm:col-span-3 text-xs text-muted-foreground">
                Last auto-publish: {config.last_publish_at ? format(new Date(config.last_publish_at), "PPp") : "—"}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Compose */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" /> Compose
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* iter-120: pre-creation image options. Applies to BOTH AI
                generators below (the 5-batch and the prompt). Default
                values give the existing behaviour (1 image, all views,
                all themes). */}
            {imageMeta && (
              <div className="rounded-md border border-white/10 bg-black/20 p-3 space-y-3" data-testid="image-options-panel">
                <Label className="text-xs uppercase text-muted-foreground flex items-center gap-1">
                  <ImageIcon className="w-3 h-3" /> Image options for the next batch
                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Number of photos per post</Label>
                    <Select
                      value={String(imageOpts.count)}
                      onValueChange={(v) => setImageOpts({ ...imageOpts, count: parseInt(v, 10) })}
                    >
                      <SelectTrigger className="mt-1" data-testid="image-count-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">0 — text only</SelectItem>
                        <SelectItem value="1">1 photo</SelectItem>
                        <SelectItem value="2">2 photos</SelectItem>
                        <SelectItem value="3">3 photos</SelectItem>
                        <SelectItem value="4">4 photos (max)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">View</Label>
                    <div className="flex gap-2">
                      {imageMeta.views.map((v) => {
                        const on = imageOpts.views.includes(v.key);
                        return (
                          <Button
                            key={v.key} variant={on ? "default" : "outline"} size="sm"
                            data-testid={`image-view-${v.key}`}
                            onClick={() => setImageOpts({
                              ...imageOpts,
                              views: on
                                ? imageOpts.views.filter((x) => x !== v.key)
                                : [...imageOpts.views, v.key],
                            })}
                          >{v.label}</Button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Theme</Label>
                    <div className="flex gap-2">
                      {imageMeta.themes.map((t) => {
                        const on = imageOpts.themes.includes(t.key);
                        return (
                          <Button
                            key={t.key} variant={on ? "default" : "outline"} size="sm"
                            data-testid={`image-theme-${t.key}`}
                            onClick={() => setImageOpts({
                              ...imageOpts,
                              themes: on
                                ? imageOpts.themes.filter((x) => x !== t.key)
                                : [...imageOpts.themes, t.key],
                            })}
                          >{t.label}</Button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label className="text-xs">App pages (leave empty to let AI pick)</Label>
                    <div className="flex flex-wrap gap-1.5">
                      {imageMeta.pages.map((p) => {
                        const on = imageOpts.pages.includes(p.key);
                        return (
                          <Button
                            key={p.key}
                            variant={on ? "default" : "outline"}
                            size="sm"
                            className="h-7 text-xs"
                            data-testid={`image-page-${p.key}`}
                            onClick={() => setImageOpts({
                              ...imageOpts,
                              pages: on
                                ? imageOpts.pages.filter((x) => x !== p.key)
                                : [...imageOpts.pages, p.key],
                            })}
                          >{p.label}</Button>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Max {imageMeta.max_count} photos per post · Multiple views/themes mix randomly so each post looks distinct.
                </p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <Button
                onClick={generate} disabled={generating}
                data-testid="generate-drafts-btn"
                className="w-full sm:w-auto"
              >
                {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                Generate 5 AI drafts
              </Button>
              {/* iter-126m: capture-pipeline diagnostic. Hit this when
                  posts come back image-less so we can pinpoint exactly
                  which step (chromium install / login / capture) is
                  failing — instead of guessing from logs. */}
              <Button
                variant="outline"
                size="sm"
                onClick={runCaptureDiagnostic}
                disabled={diagnosing}
                data-testid="diagnose-capture-btn"
                className="w-full sm:w-auto"
              >
                {diagnosing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {diagnosing ? "Running diagnostic…" : "Run capture diagnostic"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={installChromium}
                disabled={installingChromium}
                data-testid="install-chromium-btn"
                className="w-full sm:w-auto"
              >
                {installingChromium ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {installingChromium ? "Installing chromium…" : "Install chromium"}
              </Button>
              {/* iter-127f: deep pipeline test runs in background +
                  polls — never hangs the page or hits ingress timeout */}
              <Button
                variant="outline"
                size="sm"
                onClick={runDeepDiagnostic}
                disabled={runningDeep}
                data-testid="deep-diagnostic-btn"
                className="w-full sm:w-auto"
              >
                {runningDeep ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {runningDeep ? "Running full pipeline test…" : "Run full pipeline test"}
              </Button>
            </div>
            {chromiumStatus && (
              <div className="mt-2 rounded-lg border border-amber-400/30 bg-amber-500/[0.04] p-2 text-xs" data-testid="chromium-status">
                <p className="text-amber-300 font-medium">Chromium install: {chromiumStatus.running ? "running…" : chromiumStatus.result ? (chromiumStatus.result.ok ? "completed ✓" : "failed ✗") : chromiumStatus.status}</p>
                {chromiumStatus.result?.error && (
                  <p className="text-red-400 mt-1 break-all">{chromiumStatus.result.error}</p>
                )}
                {/* iter-128c: last 20 stdout lines from `playwright install
                    chromium` — surfaces the real failure cause on production
                    (permission denied, CDN 403, disk full, etc.) instead of
                    a generic "install failed" toast. */}
                {chromiumStatus.install_log?.tail?.length > 0 && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-amber-200/80 hover:text-amber-200">
                      Show install log ({chromiumStatus.install_log.tail.length} lines, rc={chromiumStatus.install_log.rc ?? "—"})
                    </summary>
                    <pre className="text-[10px] leading-tight max-h-56 overflow-auto whitespace-pre-wrap break-all mt-1 bg-black/40 p-2 rounded" data-testid="chromium-install-log">
                      {chromiumStatus.install_log.tail.join("\n")}
                    </pre>
                  </details>
                )}
              </div>
            )}
            {deepStatus && (
              <div className="mt-2 rounded-lg border border-blue-400/30 bg-blue-500/[0.04] p-2 text-xs" data-testid="deep-diagnostic-status">
                <p className="text-blue-300 font-medium">
                  Full pipeline test: {deepStatus.running ? "running in background…" : deepStatus.report ? "complete" : "queued"}
                </p>
                {deepStatus.report?.steps && (
                  <pre className="text-[11px] leading-tight max-h-64 overflow-auto whitespace-pre-wrap break-all mt-1">
                    {JSON.stringify(deepStatus.report, null, 2)}
                  </pre>
                )}
              </div>
            )}
            {diagnoseReport && (
              <div className="mt-2 rounded-lg border border-white/10 bg-slate-900/60 p-3" data-testid="diagnose-output">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">
                    Capture diagnostic — copy this back to support if anything failed
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard?.writeText(JSON.stringify(diagnoseReport, null, 2));
                      toast.success("Diagnostic copied to clipboard");
                    }}
                  >
                    Copy
                  </Button>
                </div>
                <pre className="text-[11px] leading-tight max-h-96 overflow-auto whitespace-pre-wrap break-all">
                  {JSON.stringify(diagnoseReport, null, 2)}
                </pre>
              </div>
            )}

            {/* iter-118 — AI from a user prompt. Drops into the queue as a
                regular draft and follows the same approve-before-publish flow. */}
            <div className="border-t border-white/10 pt-3 space-y-2">
              <Label className="text-xs uppercase text-muted-foreground flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Or describe your own — AI will draft it
              </Label>
              <Textarea
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                maxLength={1500}
                placeholder="e.g. Celebrate our 200th band signing up - thank everyone and mention free trial. Or: Highlight how Vivo's chat replaces 5 different WhatsApp groups for a single band."
                rows={4}
                data-testid="ai-prompt-textarea"
                disabled={promptGenerating}
              />
              <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                <Select
                  value={String(promptCount)}
                  onValueChange={(v) => setPromptCount(parseInt(v, 10))}
                  disabled={promptGenerating}
                >
                  <SelectTrigger className="sm:w-44" data-testid="ai-prompt-count">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 post</SelectItem>
                    <SelectItem value="2">2 posts</SelectItem>
                    <SelectItem value="3">3 posts</SelectItem>
                    <SelectItem value="5">5 posts</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  onClick={generateFromPrompt}
                  disabled={promptGenerating || !aiPrompt.trim()}
                  data-testid="ai-prompt-create-btn"
                >
                  {promptGenerating ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  Create post
                </Button>
                <span className="text-xs text-muted-foreground sm:ml-auto">
                  {aiPrompt.length}/1500 &middot; Saved as draft for your approval
                </span>
              </div>
            </div>

            <div className="border-t border-white/10 pt-3 space-y-2">
              <Label className="text-xs uppercase text-muted-foreground">Or write your own</Label>
              <Textarea
                value={newDraft.body}
                onChange={(e) => setNewDraft({ ...newDraft, body: e.target.value })}
                maxLength={900}
                placeholder="900 chars max. Use line breaks + 2-5 emojis to mimic AI drafts. Sign-up link auto-included if category=cta."
                rows={6}
                data-testid="new-draft-body"
              />
              <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                <Select
                  value={newDraft.category}
                  onValueChange={(v) => setNewDraft({ ...newDraft, category: v })}
                >
                  <SelectTrigger className="sm:w-48"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={createDraft} data-testid="create-draft-btn">Save draft</Button>
                <span className="text-xs text-muted-foreground sm:ml-auto">
                  {newDraft.body.length}/900
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Queue */}
        <Card>
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-sm">Queue ({posts.length})</CardTitle>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="posted">Posted</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* iter-135j — one-click retry for placeholder drafts */}
            {failedIds.length > 0 && (
              <div
                className="flex items-center justify-between gap-3 rounded-md border border-orange-500/60 bg-orange-500/10 px-3 py-2 text-sm"
                data-testid="regenerate-failed-banner"
              >
                <span className="text-orange-900 dark:text-orange-100 font-medium">
                  <strong>{failedIds.length}</strong> draft{failedIds.length === 1 ? "" : "s"} still show
                  {failedIds.length === 1 ? "s" : ""} an AI failure placeholder.
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-orange-500/70 text-orange-900 dark:text-orange-100 dark:border-orange-500/50 hover:bg-orange-500/20"
                  onClick={regenerateFailedSlots}
                  disabled={regeneratingFailed}
                  data-testid="regenerate-failed-btn"
                >
                  {regeneratingFailed ? "Regenerating…" : "Regenerate failed slots"}
                </Button>
              </div>
            )}
            {loading && <div className="text-muted-foreground text-sm">Loading…</div>}
            {!loading && posts.length === 0 && (
              <div className="text-muted-foreground text-sm">No posts in this view yet. Hit "Generate 5 AI drafts" above.</div>
            )}
            {posts.map((p) => (
              <div
                key={p.id}
                className="border border-white/10 rounded-lg p-3 space-y-2"
                data-testid={`marketing-post-${p.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={STATUS_COLORS[p.status] || ""} variant="outline">
                      {p.status}
                    </Badge>
                    <Badge variant="outline" className="text-xs">{p.category}</Badge>
                    <span className="text-[11px] text-muted-foreground">
                      by {p.created_by} · {format(new Date(p.created_at), "PP")}
                    </span>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {p.status === "draft" && (
                      <Button size="sm" variant="outline" onClick={() => approve(p)} data-testid={`approve-${p.id}`}>
                        <CheckCircle className="w-3.5 h-3.5 mr-1" /> Approve
                      </Button>
                    )}
                    {(p.status === "draft" || p.status === "approved" || p.status === "failed") && (
                      <Button size="sm" onClick={() => publishNow(p)} data-testid={`publish-${p.id}`}>
                        <Send className="w-3.5 h-3.5 mr-1" /> Post now
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => remove(p)} data-testid={`delete-${p.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                <Textarea
                  value={p.body}
                  onChange={(e) => setPosts(posts.map(x => x.id === p.id ? { ...x, body: e.target.value } : x))}
                  onBlur={() => updatePost(p, { body: p.body })}
                  maxLength={900}
                  rows={Math.max(3, Math.min(10, Math.ceil((p.body || "").length / 90)))}
                  className="text-sm bg-transparent"
                />
                {/* iter-120: multi-image gallery + per-post edit panel. */}
                {(() => {
                  const imgs = (p.images && p.images.length)
                    ? p.images
                    : (p.image_url ? [{ url: p.image_url, credit: p.image_credit }] : []);
                  return (
                    <div className="space-y-2" data-testid={`images-block-${p.id}`}>
                      {imgs.length > 0 ? (
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {imgs.map((im, idx) => (
                            <div key={idx} className="relative group rounded overflow-hidden border border-white/10 bg-black/30">
                              <img src={im.url} alt={im.credit || `image ${idx+1}`}
                                   className="w-full h-24 object-cover" loading="lazy" />
                              {(im.page || im.view || im.theme) && (
                                <div className="absolute top-1 left-1 text-[10px] bg-black/70 text-white px-1.5 py-0.5 rounded">
                                  {[im.page, im.view, im.theme].filter(Boolean).join(" · ")}
                                </div>
                              )}
                              <button
                                type="button"
                                onClick={() => removeImageFromPost(p, idx)}
                                className="absolute top-1 right-1 bg-black/70 hover:bg-red-500/90 text-white rounded p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                data-testid={`remove-image-${p.id}-${idx}`}
                                title="Remove this image"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-xs text-muted-foreground italic flex items-center gap-1">
                          <ImageIcon className="w-3 h-3" /> No photos — this will post as text only.
                        </div>
                      )}
                      <div className="flex flex-wrap gap-2 text-xs">
                        <Button
                          size="sm" variant="outline"
                          onClick={() => openImageEditor(p)}
                          data-testid={`edit-images-${p.id}`}
                          className="h-7"
                        >
                          <RefreshCw className="w-3 h-3 mr-1" /> Regenerate / change
                        </Button>
                        {imgs.length > 0 && (
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => clearImagesFromPost(p)}
                            data-testid={`clear-images-${p.id}`}
                            className="h-7"
                          >
                            Make text-only
                          </Button>
                        )}
                      </div>
                      {imageEditPostId === p.id && imageMeta && (
                        <div className="rounded-md border border-primary/30 bg-primary/5 p-3 space-y-3" data-testid={`image-editor-${p.id}`}>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs">Number of photos</Label>
                              <Select
                                value={String(imageEditOpts.count)}
                                onValueChange={(v) => setImageEditOpts({ ...imageEditOpts, count: parseInt(v, 10) })}
                              >
                                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="0">0 — text only</SelectItem>
                                  <SelectItem value="1">1 photo</SelectItem>
                                  <SelectItem value="2">2 photos</SelectItem>
                                  <SelectItem value="3">3 photos</SelectItem>
                                  <SelectItem value="4">4 photos (max)</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-1.5">
                              <Label className="text-xs">View / Theme</Label>
                              <div className="flex gap-1.5 flex-wrap">
                                {imageMeta.views.map((v) => {
                                  const on = imageEditOpts.views.includes(v.key);
                                  return (
                                    <Button key={v.key} variant={on ? "default" : "outline"} size="sm" className="h-7 text-xs"
                                      onClick={() => setImageEditOpts({ ...imageEditOpts, views: on ? imageEditOpts.views.filter(x => x !== v.key) : [...imageEditOpts.views, v.key] })}
                                    >{v.label}</Button>
                                  );
                                })}
                                {imageMeta.themes.map((t) => {
                                  const on = imageEditOpts.themes.includes(t.key);
                                  return (
                                    <Button key={t.key} variant={on ? "default" : "outline"} size="sm" className="h-7 text-xs"
                                      onClick={() => setImageEditOpts({ ...imageEditOpts, themes: on ? imageEditOpts.themes.filter(x => x !== t.key) : [...imageEditOpts.themes, t.key] })}
                                    >{t.label}</Button>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs">App pages</Label>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {imageMeta.pages.map((pg) => {
                                const on = imageEditOpts.pages.includes(pg.key);
                                return (
                                  <Button key={pg.key} variant={on ? "default" : "outline"} size="sm" className="h-6 text-xs"
                                    onClick={() => setImageEditOpts({ ...imageEditOpts, pages: on ? imageEditOpts.pages.filter(x => x !== pg.key) : [...imageEditOpts.pages, pg.key] })}
                                  >{pg.label}</Button>
                                );
                              })}
                            </div>
                          </div>
                          <div className="flex gap-2 justify-end">
                            <Button size="sm" variant="ghost" onClick={() => setImageEditPostId(null)}>Cancel</Button>
                            <Button size="sm" onClick={regenImagesForPost} disabled={imageEditSaving} data-testid={`save-images-${p.id}`}>
                              {imageEditSaving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                              Apply
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
                {p.fb_post_url && (
                  <a href={p.fb_post_url} target="_blank" rel="noreferrer" className="text-xs text-emerald-300 hover:underline">
                    View on Facebook ↗
                  </a>
                )}
                {p.last_error && (
                  <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded px-2 py-1">
                    <AlertTriangle className="w-3 h-3 inline mr-1" />
                    {p.last_error}
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
